package com.vamvisions.main.components.markdown

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.vamvisions.main.core.domain.preferences.Language
import com.vamvisions.main.core.presentation.markdown.MdInline
import com.vamvisions.main.core.presentation.markdown.MdNode
import com.vamvisions.main.core.presentation.markdown.TableCell
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.navigation.AppLink
import com.vamvisions.main.navigation.ExternalLink
import com.vamvisions.main.portfolio.presentation.common.LinkTarget
import com.vamvisions.main.portfolio.presentation.common.linkTargetOf
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Hr
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

/**
 * Renders a sanitised markdown tree as semantic HTML in an `article` declaring [language]. Every text
 * goes through `Text`, so document content is never parsed as markup. Only the first level-1 heading
 * is an `h1`; later ones render as `h2`, so the page keeps a single `h1`.
 */
@Composable
fun MarkdownContent(nodes: List<MdNode>, language: Language, onNavigate: (Route) -> Unit) {
    val blocks = remember(nodes) { nodes.withSingleH1() }
    Article(attrs = {
        classes(MarkdownStyles.article)
        attr("lang", language.code)
    }) {
        Blocks(blocks, onNavigate)
    }
}

@Composable
private fun Blocks(nodes: List<MdNode>, onNavigate: (Route) -> Unit) {
    nodes.forEach { Block(it, onNavigate) }
}

@Composable
private fun Block(node: MdNode, onNavigate: (Route) -> Unit) {
    when (node) {
        is MdNode.Heading -> when (node.level) {
            1 -> H1 { Inlines(node.inlines, onNavigate) }
            2 -> H2 { Inlines(node.inlines, onNavigate) }
            3 -> H3 { Inlines(node.inlines, onNavigate) }
            else -> H4 { Inlines(node.inlines, onNavigate) }
        }
        is MdNode.Paragraph -> P { Inlines(node.inlines, onNavigate) }
        is MdNode.BulletList -> Ul {
            node.items.forEach { item -> Li { Blocks(item, onNavigate) } }
        }
        is MdNode.OrderedList -> Ol(attrs = { if (node.start != 1) attr("start", node.start.toString()) }) {
            node.items.forEach { item -> Li { Blocks(item, onNavigate) } }
        }
        is MdNode.Image -> Img(src = node.src, alt = node.alt, attrs = { attr("loading", "lazy") })
        MdNode.Rule -> Hr()
        is MdNode.Table -> DocumentTable(node, onNavigate)
    }
}

/** The wrapper is focusable, so keyboard users can scroll a table wider than the screen. */
@Composable
private fun DocumentTable(table: MdNode.Table, onNavigate: (Route) -> Unit) {
    val label = LocalStrings.current.documentTable
    Div(attrs = {
        classes(MarkdownStyles.tableWrapper)
        attr("role", "region")
        attr("aria-label", label)
        attr("tabindex", "0")
    }) {
        Table(attrs = { classes(MarkdownStyles.table) }) {
            if (table.header.isNotEmpty()) {
                Thead {
                    Tr {
                        table.header.forEach { cell ->
                            Th(attrs = { attr("scope", "col") }) { Cell(cell, onNavigate) }
                        }
                    }
                }
            }
            Tbody {
                table.rows.forEach { row ->
                    Tr { row.forEach { cell -> Td { Cell(cell, onNavigate) } } }
                }
            }
        }
    }
}

@Composable
private fun Cell(cell: TableCell, onNavigate: (Route) -> Unit) {
    Inlines(cell.inlines, onNavigate)
}

@Composable
private fun Inlines(inlines: List<MdInline>, onNavigate: (Route) -> Unit) {
    inlines.forEach { inline ->
        when (inline) {
            is MdInline.Text -> Text(inline.value)
            is MdInline.Strong -> Strong { Inlines(inline.children, onNavigate) }
            is MdInline.Emphasis -> Em { Inlines(inline.children, onNavigate) }
            is MdInline.Code -> Code { Text(inline.value) }
            is MdInline.Link -> Link(inline, onNavigate)
            MdInline.LineBreak -> Br()
        }
    }
}

/**
 * Pages of this website navigate in place. Other site-relative paths, such as `/media/...` files,
 * and `mailto` links are plain anchors. Everything else is external and opens in a new tab.
 */
@Composable
private fun Link(link: MdInline.Link, onNavigate: (Route) -> Unit) {
    val target = if (link.href.startsWith("mailto:")) null else linkTargetOf(link.href)
    when {
        target is LinkTarget.Internal && target.route !is Route.NotFound ->
            AppLink(target.route, onNavigate) { Inlines(link.children, onNavigate) }
        target is LinkTarget.External -> ExternalLink(link.href) { Inlines(link.children, onNavigate) }
        else -> A(href = link.href) { Inlines(link.children, onNavigate) }
    }
}

/** Compose HTML has `B` but no `Strong`; `strong` carries the importance that `**text**` means. */
@Composable
private fun Strong(content: @Composable () -> Unit) {
    TagElement<HTMLElement>("strong", applyAttrs = null) { content() }
}

/** Keeps the first level-1 heading and turns every later one into level 2. */
private fun List<MdNode>.withSingleH1(): List<MdNode> {
    var seenH1 = false
    fun demote(nodes: List<MdNode>): List<MdNode> = nodes.map { node ->
        when (node) {
            is MdNode.Heading -> when {
                node.level != 1 -> node
                seenH1 -> node.copy(level = 2)
                else -> node.also { seenH1 = true }
            }
            is MdNode.BulletList -> node.copy(items = node.items.map(::demote))
            is MdNode.OrderedList -> node.copy(items = node.items.map(::demote))
            else -> node
        }
    }
    return demote(this)
}
